import { O365AuditWebView } from '../view/O365AuditWebView';
import { templateO365Audit } from '../view/template';
import { O365AuditWebComponent } from '../component/O365AuditWebComponent';
import { O365SensorController } from '../../base/o365/controller/O365SensorController';
import { IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { WEB_COMPONENT_SENSOR_O365_MAIL } from '../../../../webcomponents.constants';
import { O365AuditFields, O365MailFields } from '../../constants';

export class O365AuditWebController extends O365SensorController {
  constructor(component: O365AuditWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: O365AuditWebView,
      viewTemplate: templateO365Audit,
    });
  }
  public component: O365AuditWebComponent;
  public view: O365AuditWebView;
  public viewConstructor: typeof O365AuditWebView;

  /**
   * Reuse O365 mail fields
   */
  private reuseO365MailData(): void {
    if (
      !O365SensorController.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_MAIL) ||
      !this.isSensorEnabled() // audit sensor
    ) {
      return;
    }

    const reuseData = this.view.dom.$reuseDataWrapper.Input.checked;
    // copy value of applicationId and client secret if reuse is set
    if (reuseData) {
      O365SensorController.reuseO365Field(O365MailFields.APPLICATION, O365AuditFields.APPLICATION);
      O365SensorController.reuseO365Field(O365MailFields.CLIENT_SECRET, O365AuditFields.CLIENT_SECRET);
    }

    // copy value of tenantId
    O365SensorController.reuseO365Field(O365MailFields.TENANT, O365AuditFields.TENANT);

    this.setReadonlyToField(O365AuditFields.APPLICATION, reuseData);
    this.setReadonlyToField(O365AuditFields.TENANT, reuseData);
    this.setReadonlyToField(O365AuditFields.CLIENT_SECRET, reuseData);
  }

  computeEventListeners(): IListenerConfig[] {
    return super.computeEventListeners().concat([
      {
        element: this.view.dom.$applicationId,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$tenantId,
        event: 'change',
        callback: this.tenantIdChanged,
      },
      {
        element: this.view.dom.$clientSecret,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$reuseDataWrapper,
        event: 'change',
        callback: this.reuseChanged,
      },
    ]);
  }

  /**
   * TenantId changed event
   * @param ev
   */
  public tenantIdChanged(ev): void {
    // do not dispatch event change or reuse data if we already have same values,
    // otherwise it will go to infinite loop -> maximum call stack size exceeded
    if (O365SensorController.isSameValueIntoFields(O365AuditFields.TENANT, O365MailFields.TENANT)) {
      return;
    }

    this.inputChanged(ev);

    if (O365SensorController.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_MAIL)) {
      O365SensorController.reuseO365FieldForTenantId(ev.currentTarget.id);
    }
  }

  public toggleEnable(): void {
    super.toggleEnable();

    if (this.isSensorEnabled()) {
      if (!O365SensorController.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_MAIL)) {
        // disable reuse option if mail sensor is disabled
        this.disableReuseData(true);
      }

      // copy applicationId, tenantId and clientSecret if reuse option is set
      this.reuseO365MailData();
      return;
    }

    // disable reuse option if sensor is disabled
    this.disableReuseData(true);
  }

  public reuseChanged(ev): void {
    this.reuseO365MailData();
  }

  public postInit(ev): void {
    this.reuseO365MailData();

    // if mail has been disabled (e.g: event happened just before finishing audit init) -> remove readonly for O365 audit fields
    if (!O365SensorController.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_MAIL)) {
      this.setReadonlyToField(O365AuditFields.TENANT, false);
      this.setReadonlyToField(O365AuditFields.APPLICATION, false);
      this.setReadonlyToField(O365AuditFields.CLIENT_SECRET, false);
      this.disableReuseData(true);
    }
  }

  /**
   *
   * @param {string} fieldSelector
   * @param {boolean} readOnlyValue
   * @private
   */
  private setReadonlyToField(fieldSelector: string, readOnlyValue: boolean): void {
    switch (fieldSelector) {
      case O365AuditFields.TENANT:
        this.view.dom.$tenantId.readonly = readOnlyValue;
        break;
      case O365AuditFields.APPLICATION:
        this.view.dom.$applicationId.readonly = readOnlyValue;
        break;
      case O365AuditFields.CLIENT_SECRET:
        this.view.dom.$clientSecret.readonly = readOnlyValue;
        break;
    }
  }

  /**
   * Disabled O365 reuse data option
   * @param {boolean} disableValue
   */
  private disableReuseData(disableValue: boolean): void {
    this.view.dom.$reuseDataWrapper.Input.checked = false;
    this.view.dom.$reuseDataWrapper.disabled = disableValue;
  }
}
